import { useEffect, useLayoutEffect, useRef, type PointerEvent as ReactPointerEvent } from "react";
import { useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import { deform, mix, ribbon, rubber, slice, type Pt } from "./ribbon";
import { useArrived } from "./useArrived";
import { frameDt, settled, step, STAGGER_MS, type Spring } from "./motion";
import type { Frame, Pose, Scene, Scrub } from "./figures";

/*
  A room figure: the scene's strips as SVG paths, written straight to the DOM
  each frame so the animation never re-renders React.

  It moves one of two ways.

  - By pose. Until it is scrolled into view it shows `start` (the uncut sheet);
    once a third of it is visible the strips spring, one after another, into
    `pose`. Each later change of `pose` springs from wherever the paper is now.
  - By scroll, when it has a `scrub`. The figure's progress up the screen is the
    target of a spring, and the scrub turns that progress into a frame: strips
    fed in along their own lines, woven, wound. Scroll back and it comes apart
    the way it came. A new scrub (a new chance, the Loop going quiet) springs
    from wherever the paper is now.

  With `pull`, the paper can be taken in hand: a strip bends where it is held,
  the strips near it bend with it, and on letting go it springs back with the
  speed it was let go at. On a touch screen only a sideways pull takes it, so a
  thumb scrolling the page past the figure still scrolls the page. `pinned`
  strips (the Brane's card and its slots) stay put and cannot be taken.

  With reduced motion the figure is drawn in its final shape, changes without
  travel, and cannot be pulled.
*/

/** How far a pull carries the paper, in the 400-unit box, before it stops giving. */
const PULL_LIMIT = 70;
/** How far round the hand the paper bends. */
const PULL_REACH = 64;
/** Letting go: stiffer and looser than the figure spring, so the paper twangs back. */
const SNAP_K = 170;
const SNAP_C = 13;

/** Progress up the screen: 0 as the figure's top comes over the fold, 1 once it sits in the upper part of the screen. */
function progress(el: Element): number {
  const vh = window.innerHeight || 1;
  const top = el.getBoundingClientRect().top;
  return Math.max(0, Math.min(1, (vh * 0.92 - top) / (vh * 0.62)));
}

/** Write a path only when it changes: an unchanged write still repaints it. */
const put = (el: SVGPathElement | null | undefined, d: string) => {
  if (el && el.getAttribute("d") !== d) el.setAttribute("d", d);
};

function mixFrame(a: Frame, b: Frame, t: number): Frame {
  return {
    pose: a.pose.map((pts, i) => mix(pts, b.pose[i], t)),
    laid: b.pose.map((_, i) => {
      const x = a.laid?.[i] ?? [0, 1];
      const y = b.laid?.[i] ?? [0, 1];
      return [x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t] as [number, number];
    }),
  };
}

interface Props {
  scene: Scene;
  start: Pose;
  pose: Pose;
  /** Drive the figure by scroll instead: the frame at each moment of its progress up the screen. */
  scrub?: Scrub;
  /** Strips the figure leaves out (and their crossings): on the landing, the page's thread runs there instead. */
  omit?: number[];
  /** Let the paper be pulled by hand. */
  pull?: boolean;
  /** Strips that stay where they are when the paper is pulled, and cannot be taken in hand. */
  pinned?: number[];
  /** On letting go, with how far the paper was pulled (box units) and where it was held. */
  onRelease?: (by: Pt, at: Pt) => void;
  /** Each time the scroll reads the figure through to its end. */
  onRead?: () => void;
  /** What the figure shows, for a screen reader. Without one the figure is ornament and is hidden. */
  label?: string;
  className?: string;
  /** Called once, when the figure first arrives in view. */
  onArrive?: () => void;
}

export function PaperFigure({ scene, start, pose, scrub, omit, pull, pinned, onRelease, onRead, label, className, onArrive }: Props) {
  const svg = useRef<SVGSVGElement | null>(null);
  const strips = useRef<(SVGPathElement | null)[]>([]);
  const patches = useRef<(SVGPathElement | null)[]>([]);
  const shown = useRef<Frame>({ pose: start });
  const reduced = useReducedMotion() ?? false;
  const arrived = useArrived(svg, 0.35);
  const callbacks = useRef({ onArrive, onRelease, onRead });
  callbacks.current = { onArrive, onRelease, onRead };

  // The hand: where it holds the paper, and the pull as two springs.
  const hand = useRef<{ at: Pt; from: Pt; id: number; vx: number; vy: number; t: number } | null>(null);
  const held = useRef<[Spring, Spring]>([
    { x: 0, v: 0 },
    { x: 0, v: 0 },
  ]);
  // Where the paper was last held, for the spring back after the hand has gone.
  const lastAt = useRef<Pt>([200, 200]);
  // Whether the scroll has read the figure through since it was last scrolled back: `onRead` fires once per reading.
  const read = useRef(false);
  // Woken by the scroll and the hand; the loop below owns it.
  const wake = useRef<() => void>(() => {});

  const draw = (f: Frame) => {
    shown.current = f;
    const by: Pt = [held.current[0].x, held.current[1].x];
    const at = hand.current?.at ?? lastAt.current;
    scene.strips.forEach((s, i) => {
      const pts = pinned?.includes(i) ? f.pose[i] : deform(f.pose[i], at, by, PULL_REACH);
      const [a, b] = f.laid?.[i] ?? [0, 1];
      const path = b - a < 0.004 || omit?.includes(i) ? "" : ribbon(a <= 0 && b >= 1 ? pts : slice(pts, a, b), s.width, s.cut);
      put(strips.current[i], path);
    });
    scene.patches.forEach((patch, i) => {
      const el = patches.current[i];
      if (!el) return;
      const pts = deform(f.pose[patch.strip], at, by, PULL_REACH);
      const n = pts.length - 1;
      const [a, b] = f.laid?.[patch.strip] ?? [0, 1];
      const from = Math.max(a, patch.from / n);
      const to = Math.min(b, patch.to / n);
      put(
        el,
        to - from < 0.004 || omit?.includes(patch.strip)
          ? ""
          : ribbon(slice(pts, from, to, patch.to - patch.from + 1), scene.strips[patch.strip].width)
      );
    });
  };
  // First paint: the sheet, or the finished figure when motion is reduced.
  useLayoutEffect(() => {
    const el = svg.current;
    if (scrub) draw(scrub(reduced || !el ? 1 : progress(el)));
    else draw({ pose: reduced ? pose : start });
    // Drawn once on mount; later frames go through the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (arrived) callbacks.current.onArrive?.();
  }, [arrived]);

  // By pose: spring from wherever the paper is to `pose`, strip after strip.
  useEffect(() => {
    if (scrub || !arrived) return;
    if (reduced) {
      draw({ pose });
      return;
    }
    const el = svg.current;
    const from = shown.current.pose;
    let springs: Spring[] = pose.map(() => ({ x: 0, v: 0 }));
    let raf = 0;
    let last = performance.now();
    const began = last;
    el?.classList.add("is-moving");
    const tick = (now: number) => {
      const dt = frameDt(now, last);
      last = now;
      springs = springs.map((s, i) => (now - began < i * STAGGER_MS ? s : step(s, 1, dt)));
      held.current = held.current.map((s) => (hand.current ? s : step(s, 0, dt, SNAP_K, SNAP_C))) as [Spring, Spring];
      draw({ pose: pose.map((to, i) => mix(from[i], to, springs[i].x)) });
      if (springs.every((s) => settled(s, 1)) && !hand.current && held.current.every((s) => settled(s, 0))) {
        draw({ pose });
        el?.classList.remove("is-moving");
        raf = 0;
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    wake.current = () => {
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(tick);
    };
    return () => {
      cancelAnimationFrame(raf);
      wake.current = () => {};
      el?.classList.remove("is-moving");
    };
    // `draw` reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrived, pose, reduced, scrub]);

  // By scroll: progress up the screen is a spring's target, and the scrub draws the spring.
  useEffect(() => {
    const el = svg.current;
    if (!scrub || !el) return;
    if (reduced) {
      draw(scrub(1));
      return;
    }
    // A new scrub blends in from whatever is on the sheet now.
    const from = shown.current;
    let blend: Spring = { x: 0, v: 0 };
    let at: Spring = { x: progress(el), v: 0 };
    let target = at.x;
    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      const dt = frameDt(now, last);
      last = now;
      at = step(at, target, dt);
      blend = step(blend, 1, dt);
      held.current = held.current.map((s) => (hand.current ? s : step(s, 0, dt, SNAP_K, SNAP_C))) as [Spring, Spring];
      const p = Math.max(0, Math.min(1, at.x));
      const live = scrub(p);
      draw(settled(blend, 1) ? live : mixFrame(from, live, blend.x));
      if (p > 0.95 && !read.current) {
        read.current = true;
        callbacks.current.onRead?.();
      } else if (p < 0.6) read.current = false;
      const still = settled(at, target) && settled(blend, 1) && !hand.current && held.current.every((s) => settled(s, 0));
      raf = still ? 0 : requestAnimationFrame(tick);
    };
    const go = () => {
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(tick);
    };
    wake.current = go;
    const onScroll = () => {
      target = progress(el);
      go();
    };
    // Only listen while the figure is near the screen.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          window.addEventListener("scroll", onScroll, { passive: true });
          onScroll();
        } else window.removeEventListener("scroll", onScroll);
      },
      { rootMargin: "25% 0px" }
    );
    io.observe(el);
    window.addEventListener("resize", onScroll);
    go();
    return () => {
      io.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
      wake.current = () => {};
    };
    // `draw` reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrub, reduced]);

  const canPull = pull && !reduced;
  const toBox = (e: ReactPointerEvent): Pt => {
    const r = svg.current!.getBoundingClientRect();
    return [((e.clientX - r.left) * 400) / r.width, ((e.clientY - r.top) * 400) / r.height];
  };
  const onDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!canPull || hand.current || !(e.target as Element).closest(".sh-strip:not(.is-pinned)")) return;
    const at = toBox(e);
    hand.current = { at, from: at, id: e.pointerId, vx: 0, vy: 0, t: performance.now() };
    lastAt.current = at;
    try {
      svg.current?.setPointerCapture(e.pointerId);
    } catch {
      // A pointer the browser no longer tracks: the pull still works while it stays over the figure.
    }
    svg.current?.classList.add("is-held");
    wake.current();
  };
  const onMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    const h = hand.current;
    if (!h || e.pointerId !== h.id) return;
    const [x, y] = toBox(e);
    const now = performance.now();
    const dt = Math.max(1, now - h.t) / 1000;
    const next: [number, number] = [rubber(x - h.from[0], PULL_LIMIT), rubber(y - h.from[1], PULL_LIMIT)];
    // The paper is in the hand: it goes exactly where the hand puts it, and keeps the hand's speed for the release.
    h.vx = (next[0] - held.current[0].x) / dt;
    h.vy = (next[1] - held.current[1].x) / dt;
    h.t = now;
    held.current = [
      { x: next[0], v: h.vx },
      { x: next[1], v: h.vy },
    ];
    wake.current();
  };
  const onUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    const h = hand.current;
    if (!h || e.pointerId !== h.id) return;
    hand.current = null;
    svg.current?.classList.remove("is-held");
    callbacks.current.onRelease?.([held.current[0].x, held.current[1].x], h.at);
    wake.current();
  };

  return (
    <svg
      ref={svg}
      viewBox="0 0 400 400"
      className={cn("sh-figure", canPull && "is-pullable", className)}
      onPointerDown={canPull ? onDown : undefined}
      onPointerMove={canPull ? onMove : undefined}
      onPointerUp={canPull ? onUp : undefined}
      onPointerCancel={canPull ? onUp : undefined}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      {scene.strips.map((s, i) => (
        <path
          key={`s${i}`}
          ref={(el) => {
            strips.current[i] = el;
          }}
          className={cn(`sh-strip sh-strip--${s.tone}`, pinned?.includes(i) && "is-pinned")}
        />
      ))}
      {scene.patches.map((patch, i) => (
        <path
          key={`p${i}`}
          ref={(el) => {
            patches.current[i] = el;
          }}
          className={`sh-strip sh-strip--${scene.strips[patch.strip].tone} sh-strip--patch`}
        />
      ))}
    </svg>
  );
}
